import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ComplianceFooter from "@/components/landing/ComplianceFooter";
import { MDMonogram } from "@/components/ui/system-primitives";
import PasswordInput from "@/components/PasswordInput";
import PasswordRequirements from "@/components/PasswordRequirements";
import PhoneCountryInput from "@/components/PhoneCountryInput";
import { COUNTRIES, isValidNationalNumber } from "@/data/phoneCountries";
import { nextSignupQuote } from "@/data/loginQuotes";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { ArrowLeft, Copy, Download, Lock, ShieldCheck } from "lucide-react";
import { passwordMeetsPolicy } from "@/lib/passwordPolicy";

const TITLES = ["Mr", "Mrs", "Miss", "Dr", "Prof"];

export default function Register() {
    const { register, error, setError, beginGoogle, refresh } = useAuth();
    const nav = useNavigate();
    const recaptcha = useRecaptcha();
    const [params] = useSearchParams();
    const [busy, setBusy] = useState(false);
    const [recoveryCodes, setRecoveryCodes] = useState(null);   // shown once after signup
    const [ackSaved, setAckSaved] = useState(false);            // user must confirm they saved them
    const [referrerName, setReferrerName] = useState(null);
    const [isObondoTrial, setIsObondoTrial] = useState(true); // every signup gets the 7-day trial (2026-09-01)
    const [confirmPassword, setConfirmPassword] = useState("");
    const [dialCode, setDialCode] = useState("+254");
    const [nationalNumber, setNationalNumber] = useState("");
    const [phoneTouched, setPhoneTouched] = useState(false);
    // Beginner-weighted quote deck — rotates on every mount, persisted in
    // localStorage under `md:signup-quote-idx` (separate from the login
    // deck so rotations don't interfere).
    const quote = useMemo(() => nextSignupQuote(), []);
    const [form, setForm] = useState({
        title: "", first_name: "", last_name: "", email: "", password: "",
        nationality: "", country_of_residence: "", is_whatsapp: false,
        tos_accepted: false, referral_code: "",
    });

    useEffect(() => {
        const ref = params.get("ref");
        if (params.get("intent") === "obondo_trial") setIsObondoTrial(true);
        if (!ref) return;
        setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
        api.get(`/referral/lookup/${ref.toUpperCase()}`)
            .then((r) => r?.data && setReferrerName(r.data.name))
            .catch(() => {});
    }, [params]);

    const update = (k) => (e) => {
        const v = e.target ? e.target.value : e;
        setForm((f) => ({ ...f, [k]: v }));
    };

    async function submit(e) {
        e.preventDefault();
        setError(null);
        setPhoneTouched(true);
        // Guard: password must meet the strict complexity policy —
        // 8+ chars, 1 uppercase, 1 number, 1 special character.
        if (!passwordMeetsPolicy(form.password)) {
            setError("Password must be at least 8 characters and include 1 uppercase letter, 1 number, and 1 special character.");
            return;
        }
        // Guard: passwords must match. Backend can't check this since it only
        // sees `password` — enforcing here prevents typo-locked accounts.
        if (form.password !== confirmPassword) {
            setError("Passwords do not match. Please re-enter them.");
            return;
        }
        // Guard: phone number must match the selected country's format.
        if (!isValidNationalNumber(dialCode, nationalNumber)) {
            setError("Enter a valid telephone number for the selected country code.");
            return;
        }
        setBusy(true);
        const payload = {
            ...form,
            phone_dial_code: dialCode,
            phone_number: nationalNumber,
            // Activates the 7-day Obondo trial server-side immediately —
            // no card required. See routes/auth.py::register().
            intent: isObondoTrial ? "obondo_trial" : undefined,
        };
        // reCAPTCHA v3 — invisible bot score. Backend fails open if key not
        // configured, so this is a no-op in local/dev.
        const rcToken = await recaptcha.execute("register");
        const res = await register(payload, rcToken);
        setBusy(false);
        if (res) {
            const codes = Array.isArray(res.recovery_codes) ? res.recovery_codes : [];
            // Auto-verify mode — every signup now returns recovery codes and
            // an active session cookie. We deliberately do NOT hydrate
            // AuthContext here (that would fire the policy-acceptance guard
            // and unmount this card, losing the once-shown codes). Hydration
            // happens INSIDE the 'Proceed to Dashboard' onClick after ack.
            setRecoveryCodes(codes);
            toast.success("Account created! Save your recovery codes below.");
        }
    }

    // Recovery-code confirmation screen — shown once at signup.
    // Feb 2026 upgrade: full "Ultimate Recovery Master Keys" security card
    // with copy-to-clipboard, PDF download, and a mandatory-friction ack.
    if (recoveryCodes && recoveryCodes.length > 0) {
        const txt = recoveryCodes.join("\n");

        const onCopy = async () => {
            try {
                await navigator.clipboard.writeText(txt);
                toast.success("10 recovery codes copied to clipboard.");
            } catch {
                // Fallback: transient textarea + document.execCommand for
                // headless / insecure / denied-permission contexts.
                try {
                    const ta = document.createElement("textarea");
                    ta.value = txt;
                    ta.setAttribute("readonly", "");
                    ta.style.position = "fixed";
                    ta.style.top = "-1000px";
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand("copy");
                    document.body.removeChild(ta);
                    toast.success("10 recovery codes copied to clipboard.");
                } catch {
                    toast.error("Copy failed — please select the codes manually.");
                }
            }
        };

        const onDownloadPdf = () => {
            const doc = new jsPDF({ unit: "pt", format: "a4" });
            const marginX = 56;
            let y = 72;
            // Title
            doc.setFont("helvetica", "bold");
            doc.setFontSize(20);
            doc.setTextColor(146, 37, 41); // brand maroon
            doc.text("Melian Dialogue", marginX, y); y += 24;
            doc.setFontSize(14);
            doc.setTextColor(30, 30, 30);
            doc.text("Ultimate Recovery Master Keys", marginX, y); y += 28;

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.setTextColor(70, 70, 70);
            const intro = doc.splitTextToSize(
                "These 10 single-use codes are the ONLY way to regain access to your account if you switch away from Google Sign-In or lose your primary login state. Store this file offline in a safe place. Each code works exactly once.",
                480,
            );
            doc.text(intro, marginX, y); y += intro.length * 14 + 12;

            // Codes — 2-column grid
            doc.setFont("courier", "bold");
            doc.setFontSize(14);
            doc.setTextColor(30, 30, 30);
            const colWidth = 240;
            for (let i = 0; i < recoveryCodes.length; i++) {
                const col = i % 2;
                const row = Math.floor(i / 2);
                const x = marginX + col * colWidth;
                const yy = y + row * 26;
                doc.text(`${String(i + 1).padStart(2, "0")}.  ${recoveryCodes[i]}`, x, yy);
            }
            y += Math.ceil(recoveryCodes.length / 2) * 26 + 30;

            // Footer
            doc.setFont("helvetica", "italic");
            doc.setFontSize(9);
            doc.setTextColor(120, 120, 120);
            const footer = doc.splitTextToSize(
                "Melian Dialogue Limited · Nairobi, Kenya · Zero-Trust closed-loop recovery. Support cannot reset your account without a valid recovery code.",
                480,
            );
            doc.text(footer, marginX, y);

            doc.save("melian-recovery-codes.pdf");
            toast.success("Recovery codes PDF downloaded.");
        };

        return (
            <div className="min-h-screen flex flex-col bg-gradient-to-b from-primary/5 via-background to-background">
                <div className="flex-1 flex items-center justify-center p-4 md:p-6">
                    <div className="max-w-xl w-full rounded-2xl border border-primary/30 bg-card shadow-2xl overflow-hidden"
                         data-testid="recovery-master-keys-card">
                        {/* Header band */}
                        <div className="px-6 py-5 border-b border-border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shadow-md">
                                    <Lock className="w-5 h-5" strokeWidth={2.25} />
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-primary">
                                    Zero-Trust · Self-Custody
                                </div>
                            </div>
                            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight leading-tight"
                                data-testid="recovery-master-heading">
                                🔒 Your Ultimate Recovery Master Keys
                            </h1>
                        </div>

                        {/* Body */}
                        <div className="p-6">
                            <p className="text-sm leading-relaxed text-muted-foreground mb-5"
                               data-testid="recovery-master-subtext">
                                You can seamlessly log in anytime using your Google mail account via <b>Google Sign-In</b>.
                                However, to maintain a completely secure, self-custodied platform, we do <b>not</b> use
                                insecure SMS OTPs, vulnerable email links, or password-reset chains. These <b>10 codes</b>
                                are the ONLY way to regain access to your profile if you switch to standard email entry
                                or lose your primary login state.
                            </p>

                            {/* 2-column code grid */}
                            <div className="grid grid-cols-2 gap-2 mb-5 p-3 rounded-xl border border-dashed border-primary/30 bg-primary/[0.03]"
                                 data-testid="recovery-codes-grid">
                                {recoveryCodes.map((c, i) => (
                                    <div key={c}
                                         className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-background border border-border font-mono text-[13px] proportional-nums"
                                         data-testid={`recovery-code-${i}`}>
                                        <span className="text-[10px] font-bold text-muted-foreground shrink-0 w-5">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="font-bold truncate" data-testid={`recovery-code-value-${i}`}>{c}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col sm:flex-row gap-2 mb-5">
                                <Button
                                    variant="outline"
                                    className="flex-1 gap-2"
                                    onClick={onCopy}
                                    data-testid="recovery-copy"
                                >
                                    <Copy className="w-4 h-4" /> Copy to Clipboard
                                </Button>
                                <Button
                                    variant="outline"
                                    className="flex-1 gap-2"
                                    onClick={onDownloadPdf}
                                    data-testid="recovery-download-pdf"
                                >
                                    <Download className="w-4 h-4" /> Download Backup PDF
                                </Button>
                            </div>

                            {/* Mandatory ack */}
                            <label className="flex items-start gap-2.5 mb-5 p-3 rounded-lg border border-border bg-muted/30 cursor-pointer">
                                <Checkbox
                                    id="recovery-ack"
                                    checked={ackSaved}
                                    onCheckedChange={(v) => setAckSaved(!!v)}
                                    data-testid="recovery-ack"
                                    className="mt-0.5"
                                />
                                <span className="text-xs leading-relaxed">
                                    <b>I have securely saved these 10 backup codes offline. I understand that if I lose them, the support team cannot reset my account.</b>
                                </span>
                            </label>

                            {/* Primary CTA — disabled until ack. Also re-hydrates
                                AuthContext right before nav so the /app guard
                                sees the fresh session cookie. */}
                            <Button
                                className="w-full gap-2"
                                disabled={!ackSaved}
                                onClick={async () => {
                                    try { await refresh(); } catch { /* ignore — cookie still valid */ }
                                    nav("/app");
                                }}
                                data-testid="recovery-proceed"
                            >
                                <ShieldCheck className="w-4 h-4" />
                                Proceed to Dashboard
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (recoveryCodes && recoveryCodes.length === 0) {
        // Rare fallback if the server returned an empty codes array — send the
        // user straight to sign-in rather than stranding them on a blank screen.
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <div className="max-w-md text-center">
                    <h1 className="text-2xl font-extrabold mb-2">Account created</h1>
                    <p className="text-sm text-muted-foreground mb-4">You're all set. Sign in to enter the terminal.</p>
                    <Link to="/login"><Button className="w-full">Continue to sign in</Button></Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col">
            <div className="flex-1 grid md:grid-cols-2">
                <div className="hidden md:block relative">
                    <img
                        src="https://customer-assets-v7afamib.emergentagent.net/job_copy-trade-hub-11/artifacts/bm9bsgq5_images.jpeg"
                        alt="Melian Dialogue new trader" loading="eager"
                        className="w-full h-full object-cover"
                        data-testid="register-hero-image"
                    />
                    <div className="absolute inset-0 bg-primary/10 backdrop-blur-[1px]" />
                    <div className="absolute bottom-8 left-8 right-8 backdrop-blur-md bg-background/70 p-6 rounded-lg border border-border max-w-md" data-testid="register-quote-card">
                        <div className="overline text-primary mb-2 flex items-center gap-2" data-testid="register-quote-author">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                            {quote.author}
                        </div>
                        <div className="text-2xl font-bold leading-tight tracking-tight" data-testid="register-quote-text">
                            &ldquo;{quote.text}&rdquo;
                        </div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-3">
                            Quote of the day.
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-center p-6 md:p-10 overflow-y-auto">
                    <div className="w-full max-w-md">
                        <div className="flex items-center justify-between mb-6">
                            <Link to="/" className="flex items-center gap-2" data-testid="register-logo">
                                <MDMonogram size="md" testid="register-monogram" />
                                <div className="font-heading font-extrabold tracking-tight text-lg">Melian&nbsp;Dialogue<span className="text-primary">.</span></div>
                            </Link>
                            <button
                                type="button"
                                onClick={() => nav(-1)}
                                data-testid="register-back-btn"
                                className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg border border-input text-sm font-semibold hover:bg-secondary transition-colors focus-visible:ring-2 focus-visible:ring-[#006600] dark:focus-visible:ring-[#4ADE80]"
                            >
                                <ArrowLeft className="w-4 h-4" /> Back
                            </button>
                        </div>
                        <div className="overline mb-3 text-primary" data-testid="register-eyebrow">Create your account</div>
                        <h1 className="font-heading text-h1 mb-2" data-testid="register-h1">
                            {isObondoTrial
                                ? <>Start your <span className="text-primary">Obondo trial</span>.</>
                                : <>Start <span className="text-primary">free</span>.</>}
                        </h1>
                        <p className="font-body text-body text-muted-foreground mb-5">
                            {isObondoTrial
                                ? "7 days of full Obondo Advanced access · No card required."
                                : <>Individual accounts are free forever &middot; No trial &middot; No card required.</>}
                        </p>

                        <button
                            type="button"
                            onClick={beginGoogle}
                            data-testid="reg-google"
                            className="w-full mb-4 min-h-[44px] inline-flex items-center justify-center gap-2 border border-border bg-card hover:bg-secondary text-foreground rounded-lg py-2.5 text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-[#006600] dark:focus-visible:ring-[#4ADE80]"
                        >
                            <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z"/>
                                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                            </svg>
                            Continue with Google
                        </button>
                        <div className="relative my-4">
                            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                            <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
                                <span className="bg-background px-2 text-muted-foreground">or with email</span>
                            </div>
                        </div>

                        {isObondoTrial && (
                            <div className="mb-5 border border-primary/40 bg-primary/10 rounded-lg p-3 text-sm" data-testid="obondo-trial-banner">
                                <div className="overline text-primary mb-0.5">7-Day Obondo Advanced Free Trial</div>
                                <div>No card required. Full Obondo access unlocks the moment you create your account — you'll get a reminder before day 7 if you'd like to continue.</div>
                            </div>
                        )}

                        {referrerName && (
                            <div className="mb-5 border border-accent/40 bg-accent/10 rounded-lg p-3 text-sm" data-testid="referrer-banner">
                                <div className="overline text-accent mb-0.5">Referred by</div>
                                <div><span className="font-semibold">{referrerName}</span> · You'll both earn 7 free days once you subscribe.</div>
                            </div>
                        )}

                        <form onSubmit={submit} className="space-y-3">
                            <div className="grid grid-cols-[110px_1fr] gap-2">
                                <div>
                                    <Label htmlFor="title">Title</Label>
                                    <Select value={form.title} onValueChange={(v) => setForm((f) => ({ ...f, title: v }))}>
                                        <SelectTrigger id="title" className="h-11 text-[16px] rounded-lg focus-visible:ring-2 focus-visible:ring-[#006600] dark:focus-visible:ring-[#4ADE80]" data-testid="reg-title">
                                            <SelectValue placeholder="Title" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {TITLES.map((t) => <SelectItem key={t} value={t} data-testid={`reg-title-${t.toLowerCase()}`}>{t}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label htmlFor="first_name">First name</Label>
                                    <Input id="first_name" required value={form.first_name} onChange={update("first_name")}
                                           className="h-11 text-[16px] rounded-lg" data-testid="reg-first-name" />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="last_name">Last name</Label>
                                <Input id="last_name" required value={form.last_name} onChange={update("last_name")}
                                       className="h-11 text-[16px] rounded-lg" data-testid="reg-last-name" />
                            </div>
                            <div>
                                <Label htmlFor="email">Email</Label>
                                <Input id="email" type="email" required value={form.email} onChange={update("email")}
                                       className="h-11 text-[16px] rounded-lg" data-testid="reg-email" />
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label htmlFor="nationality">Nationality</Label>
                                    <Select value={form.nationality} onValueChange={(v) => setForm((f) => ({ ...f, nationality: v }))}>
                                        <SelectTrigger id="nationality" className="h-11 text-[16px] rounded-lg focus-visible:ring-2 focus-visible:ring-[#006600] dark:focus-visible:ring-[#4ADE80]" data-testid="reg-nationality">
                                            <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-72">
                                            {COUNTRIES.map((c) => <SelectItem key={c.iso2} value={c.name} data-testid={`reg-nationality-${c.iso2}`}>{c.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label htmlFor="country_of_residence">Country of Residence</Label>
                                    <Select value={form.country_of_residence} onValueChange={(v) => setForm((f) => ({ ...f, country_of_residence: v }))}>
                                        <SelectTrigger id="country_of_residence" className="h-11 text-[16px] rounded-lg focus-visible:ring-2 focus-visible:ring-[#006600] dark:focus-visible:ring-[#4ADE80]" data-testid="reg-country-residence">
                                            <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-72">
                                            {COUNTRIES.map((c) => <SelectItem key={c.iso2} value={c.name} data-testid={`reg-residence-${c.iso2}`}>{c.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="phone">Telephone</Label>
                                <PhoneCountryInput
                                    dialCode={dialCode}
                                    nationalNumber={nationalNumber}
                                    onDialChange={setDialCode}
                                    onNumberChange={(v) => { setNationalNumber(v); setPhoneTouched(true); }}
                                    testidPrefix="reg-phone"
                                    touched={phoneTouched}
                                />
                            </div>

                            <label className="flex items-start gap-2 py-1 text-xs text-muted-foreground cursor-pointer">
                                <Checkbox
                                    id="is_whatsapp"
                                    checked={form.is_whatsapp}
                                    onCheckedChange={(v) => setForm((f) => ({ ...f, is_whatsapp: !!v }))}
                                    data-testid="reg-is-whatsapp"
                                />
                                <span>Check if this is also your WhatsApp number.</span>
                            </label>

                            <div>
                                <Label htmlFor="password">Password</Label>
                                <PasswordInput
                                    id="password"
                                    minLength={8}
                                    required
                                    value={form.password}
                                    onChange={update("password")}
                                    testId="reg-password"
                                    toggleTestId="reg-password-toggle"
                                    className="h-11 text-[16px] rounded-lg"
                                />
                                <PasswordRequirements password={form.password} testId="reg-password-requirements" />
                            </div>
                            <div>
                                <Label htmlFor="confirm_password">Confirm password</Label>
                                <PasswordInput
                                    id="confirm_password"
                                    minLength={8}
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    testId="reg-confirm-password"
                                    toggleTestId="reg-confirm-password-toggle"
                                    aria-invalid={confirmPassword && confirmPassword !== form.password ? "true" : "false"}
                                    className="h-11 text-[16px] rounded-lg"
                                />
                                {confirmPassword && confirmPassword !== form.password && (
                                    <p className="text-xs text-destructive mt-1" data-testid="reg-password-mismatch">
                                        Passwords don't match yet.
                                    </p>
                                )}
                                {confirmPassword && confirmPassword === form.password && form.password.length >= 8 && (
                                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1" data-testid="reg-password-match">
                                        Passwords match.
                                    </p>
                                )}
                            </div>
                            <div>
                                <Label htmlFor="referral_code">Referral code (optional)</Label>
                                <Input id="referral_code" value={form.referral_code} onChange={update("referral_code")}
                                       className="h-11 text-[16px] rounded-lg" data-testid="reg-referral" />
                            </div>

                            <label className="flex items-start gap-2 pt-2 text-xs text-muted-foreground cursor-pointer">
                                <Checkbox
                                    id="tos"
                                    checked={form.tos_accepted}
                                    onCheckedChange={(v) => setForm((f) => ({ ...f, tos_accepted: !!v }))}
                                    data-testid="reg-tos"
                                />
                                <span>
                                    I accept the <span className="font-semibold text-foreground">Terms of Service</span> and explicitly acknowledge the <span className="font-semibold text-foreground">5-day minimum trading constraint</span>.
                                </span>
                            </label>

                            {error && <div className="text-danger text-sm" data-testid="reg-error">{error}</div>}

                            <Button type="submit" className="w-full min-h-[44px] rounded-lg" disabled={busy || !form.tos_accepted} data-testid="reg-submit">
                                {busy ? "Creating…" : "Create free account & get recovery codes"}
                            </Button>

                            <div className="text-xs text-muted-foreground text-center">
                                Already have one? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <ComplianceFooter />
        </div>
    );
}
